import React, { useState, useEffect, useCallback } from 'react';
import { appointmentAPI, patientAPI, doctorAPI, staffAPI } from '../../lib/api';
import { formatDate, getStatusColor, apiErrorMessage } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { CalendarDays, Plus, Clock, User, Phone, Check, X, CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { logger } from '../../lib/logger';

const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00'
];

const APPOINTMENT_TYPES = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'check_up', label: 'General Check-up' },
];

const AppointmentList = () => {
  const { user, hasRole } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    patient_id: '',
    doctor_id: '',
    appointment_date: '',
    appointment_time: '',
    reason: '',
    appointment_type: 'consultation',
    notes: '',
  });

  useEffect(() => {
    if (formData.appointment_date) {
      fetchAvailableDoctors(formData.appointment_date);
    }
  }, [formData.appointment_date]);

  const fetchData = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const params = { date: dateStr };
      if (statusFilter !== 'all') params.status = statusFilter;

      const [aptRes, todayRes, patRes, docRes] = await Promise.all([
        appointmentAPI.getAll(params),
        appointmentAPI.getToday(),
        patientAPI.getAll({}),
        doctorAPI.getAll(),
      ]);
      
      setAppointments(aptRes.data);
      setTodayAppointments(todayRes.data);
      setPatients(patRes.data.patients || patRes.data || []);
      setDoctors(docRes.data);
    } catch (error) {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, statusFilter]);


  useEffect(() => {
    fetchData();
  }, [selectedDate, statusFilter, fetchData]);
  const fetchAvailableDoctors = async (date) => {
    try {
      const response = await staffAPI.getAvailability(date);
      const available = response.data.filter(s => s.role === 'doctor' && s.is_available);
      setAvailableDoctors(available);
    } catch (error) {
      logger.error('Failed to fetch availability');
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setFormData({ ...formData, appointment_date: format(date, 'yyyy-MM-dd') });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        doctor_id: hasRole('doctor') ? user.id : formData.doctor_id,
      };
      await appointmentAPI.create(data);
      toast.success('Appointment scheduled successfully');
      setDialogOpen(false);
      setFormData({
        patient_id: '',
        doctor_id: '',
        appointment_date: '',
        appointment_time: '',
        reason: '',
        appointment_type: 'consultation',
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to create appointment'));
    }
  };

  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      await appointmentAPI.update(appointmentId, { status: newStatus });
      toast.success('Appointment status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-emerald-100 text-emerald-800',
      in_progress: 'bg-amber-100 text-amber-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-slate-100 text-slate-800',
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  return (
    <div data-testid="appointments-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Appointments</h1>
            <p className="text-slate-500">Schedule and manage patient appointments</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="new-appointment-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Appointment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-[Manrope]">Schedule Appointment</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') e.preventDefault(); }}>
                <div>
                  <Label>Patient *</Label>
                  <Select value={formData.patient_id} onValueChange={(v) => setFormData({...formData, patient_id: v})}>
                    <SelectTrigger data-testid="select-patient">
                      <SelectValue placeholder="Select patient" />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.full_name} ({p.patient_id})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {!hasRole('doctor') && (
                  <div>
                    <Label>Doctor *</Label>
                    <Select value={formData.doctor_id} onValueChange={(v) => setFormData({...formData, doctor_id: v})}>
                      <SelectTrigger data-testid="select-doctor">
                        <SelectValue placeholder="Select doctor" />
                      </SelectTrigger>
                      <SelectContent>
                        {doctors.map(d => (
                          <SelectItem key={d.id} value={d.id}>Dr. {d.full_name} - {d.department}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date *</Label>
                    <Popover modal={false}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="w-full justify-start text-left font-normal" data-testid="select-date">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.appointment_date ? format(new Date(formData.appointment_date), 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start" disablePortal={true}>
                        <Calendar
                          mode="single"
                          selected={formData.appointment_date ? new Date(formData.appointment_date) : undefined}
                          onSelect={(date) => date && setFormData({...formData, appointment_date: format(date, 'yyyy-MM-dd')})}
                          disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Time *</Label>
                    <Select value={formData.appointment_time} onValueChange={(v) => setFormData({...formData, appointment_time: v})}>
                      <SelectTrigger data-testid="select-time">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={5}>
                        {TIME_SLOTS.map(time => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Appointment Type *</Label>
                  <Select value={formData.appointment_type} onValueChange={(v) => setFormData({...formData, appointment_type: v})}>
                    <SelectTrigger data-testid="select-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {APPOINTMENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Reason for Visit *</Label>
                  <Input
                    value={formData.reason}
                    onChange={(e) => setFormData({...formData, reason: e.target.value})}
                    placeholder="e.g., Fever, headache for 3 days"
                    required
                    data-testid="input-reason"
                  />
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Any additional notes"
                    data-testid="input-notes"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="submit-appointment">Schedule</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="page-content">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar and Today's Appointments */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-red-500" />
                  Select Date
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && handleDateSelect(date)}
                  className="rounded-md border"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold font-[Manrope]">
                  Today's Appointments ({todayAppointments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todayAppointments.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No appointments today</p>
                ) : (
                  <div className="space-y-3">
                    {todayAppointments.slice(0, 5).map((apt) => (
                      <div key={apt.id} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{apt.patient?.full_name}</span>
                          <Badge className={getStatusBadgeColor(apt.status)} variant="secondary">
                            {apt.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {apt.appointment_time}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            Dr. {apt.doctor?.full_name}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Appointments List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold font-[Manrope]">
                    Appointments for {format(selectedDate, 'PPP')}
                  </CardTitle>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40" data-testid="status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-slate-500">Loading appointments...</div>
                ) : appointments.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No appointments for this date.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Patient</TableHead>
                        <TableHead>Doctor</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appointments.map((apt) => (
                        <TableRow key={apt.id} data-testid={`appointment-row-${apt.id}`}>
                          <TableCell className="font-medium">{apt.appointment_time}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{apt.patient?.full_name}</p>
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {apt.patient?.phone}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>Dr. {apt.doctor?.full_name}</TableCell>
                          <TableCell className="capitalize">{apt.appointment_type?.replace('_', ' ')}</TableCell>
                          <TableCell>
                            <Badge className={getStatusBadgeColor(apt.status)}>{apt.status}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {apt.status === 'scheduled' && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleStatusUpdate(apt.id, 'confirmed')}
                                    className="text-emerald-600"
                                    data-testid={`confirm-${apt.id}`}
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleStatusUpdate(apt.id, 'cancelled')}
                                    className="text-red-600"
                                    data-testid={`cancel-${apt.id}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              {apt.status === 'confirmed' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleStatusUpdate(apt.id, 'completed')}
                                  data-testid={`complete-${apt.id}`}
                                >
                                  Complete
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentList;
